import type {
    ChangeEvent,
    FC,
    FormEvent,
    MouseEvent,
    ReactNode
} from 'react'
import type { InquiryPayload } from '@/types'
import type { InquiryErrors } from '@/lib/inquiry'

import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Check, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
    EMPTY_INQUIRY,
    toDateString,
    validateInquiry
} from '@/lib/inquiry'

interface BookingDialogProps {
    open: boolean
    onClose: () => void
}

interface FieldProps {
    id: keyof InquiryPayload
    label: string
    error?: string
    optional?: boolean
    className?: string
    children: ReactNode
}

type Status = 'idle' | 'sending' | 'sent' | 'error'

const FIELD_ORDER: Array<keyof InquiryPayload> = [
    'fullName',
    'email',
    'phone',
    'departure',
    'destination',
    'travelDate',
    'passengers'
]

const inputClass = 'h-12 w-full border-0 border-b border-champagne/25 bg-transparent px-0 text-[15px] text-ivory placeholder:text-smoke/70 transition-colors focus:border-champagne focus:outline-none aria-[invalid=true]:border-[#e39a8c]'

const Field: FC<FieldProps> = ({
    id,
    label,
    error,
    optional,
    className,
    children
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <label htmlFor={`booking-${id}`} className='text-[11px] uppercase tracking-[0.2em] text-smoke'>
                {label}
                {optional && <span className='ml-2 normal-case tracking-normal text-smoke/70'>(optional)</span>}
            </label>
            {children}
            {error && (
                <p id={`booking-${id}-error`} className='text-[12px] text-[#e39a8c]'>
                    {error}
                </p>
            )}
        </div>
    )
}

const BookingDialog: FC<BookingDialogProps> = ({ open, onClose }): ReactNode => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const formRef = useRef<HTMLFormElement>(null)
    const [values, setValues] = useState<InquiryPayload>(EMPTY_INQUIRY)
    const [errors, setErrors] = useState<InquiryErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [minDate, setMinDate] = useState('')
    const statusRef = useRef<Status>('idle')

    useEffect(() => {
        statusRef.current = status
    }, [status])

    useEffect(() => {
        const dialog = dialogRef.current
        if (!dialog) return

        if (open && !dialog.open) {
            setMinDate(toDateString(new Date()))
            dialog.showModal()
        }

        if (!open && dialog.open) {
            dialog.close()
        }
    }, [open])

    useEffect(() => {
        const dialog = dialogRef.current
        if (!dialog) return

        const handleClose = (): void => {
            onClose()

            if (statusRef.current === 'sent') {
                setValues(EMPTY_INQUIRY)
                setErrors({})
            }

            if (statusRef.current !== 'sending') {
                setStatus('idle')
                setMessage('')
            }
        }

        dialog.addEventListener('close', handleClose)
        return () => dialog.removeEventListener('close', handleClose)
    }, [onClose])

    const update = (field: keyof InquiryPayload) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        const value = field === 'passengers' ? Number(event.target.value) : event.target.value
        setValues((current) => ({ ...current, [field]: value }))
        if (errors[field]) {
            setErrors((current) => ({ ...current, [field]: undefined }))
        }
    }

    const focusFirstError = (found: InquiryErrors): void => {
        const first = FIELD_ORDER.find((field) => found[field])
        if (!first) return
        formRef.current?.querySelector<HTMLElement>(`#booking-${first}`)?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const result = validateInquiry(values, minDate)
        setErrors(result.errors)

        if (Object.keys(result.errors).length > 0) {
            focusFirstError(result.errors)
            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/inquiries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(result.values)
            })

            const data = (await response.json().catch(() => ({}))) as { error?: string; fields?: InquiryErrors }

            if (!response.ok) {
                if (data.fields) {
                    setErrors(data.fields)
                    focusFirstError(data.fields)
                }
                setMessage(data.error ?? "We couldn't send your request just now. Please try again in a moment.")
                setStatus('error')
                return
            }

            setStatus('sent')
        } catch {
            setMessage("We couldn't reach our team just now. Please check your connection and try again.")
            setStatus('error')
        }
    }

    const handleBackdrop = (event: MouseEvent<HTMLDialogElement>): void => {
        if (event.target === dialogRef.current) {
            dialogRef.current?.close()
        }
    }

    const describedBy = (field: keyof InquiryPayload): string | undefined => (errors[field] ? `booking-${field}-error` : undefined)

    return (
        <dialog
            ref={dialogRef}
            aria-labelledby='booking-title'
            data-lenis-prevent
            onClick={handleBackdrop}
            className='booking-dialog m-auto max-h-[92svh] w-[min(94vw,760px)] overflow-y-auto rounded-[20px] border border-champagne/20 bg-onyx p-0 text-ivory shadow-[0_40px_120px_rgba(0,0,0,0.6)]'
        >
            <div className='relative px-6 pb-8 pt-7 md:px-12 md:pb-12 md:pt-11'>
                <button
                    type='button'
                    onClick={() => dialogRef.current?.close()}
                    aria-label='Close'
                    className='absolute right-4 top-4 flex size-11 items-center justify-center rounded-full border border-champagne/20 text-champagne transition-colors hover:bg-champagne hover:text-obsidian focus-visible:outline focus-visible:outline-2 focus-visible:outline-champagne md:right-6 md:top-6'
                >
                    <X className='size-5' />
                </button>

                {status === 'sent' ? (
                    <div className='flex min-h-[420px] flex-col items-start justify-center gap-6 pr-10' role='status'>
                        <span className='flex size-14 items-center justify-center rounded-full border border-champagne/40 text-champagne'>
                            <Check className='size-6' />
                        </span>
                        <h2 id='booking-title' className='font-heading text-[clamp(2.2rem,5vw,3.4rem)] font-light leading-[1.02]'>
                            Thank you, your request is with us.
                        </h2>
                        <p className='max-w-md text-[15px] leading-relaxed text-ivory/70'>
                            A member of the AERIVE flight team will reply personally to <span className='text-champagne'>{values.email}</span> with options for your journey.
                        </p>
                        <button
                            type='button'
                            onClick={() => dialogRef.current?.close()}
                            className='mt-2 inline-flex h-12 items-center gap-3 rounded-full border border-champagne/40 px-7 text-[12px] uppercase tracking-[0.22em] text-champagne transition-colors hover:bg-champagne hover:text-obsidian'
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <>
                        <p className='text-[11px] uppercase tracking-[0.25em] text-champagne'>Book a flight</p>
                        <h2 id='booking-title' className='mt-3 max-w-[16ch] pr-10 font-heading text-[clamp(2.2rem,5vw,3.4rem)] font-light leading-[1.02]'>
                            Where would you like to go?
                        </h2>
                        <p className='mt-4 max-w-md text-[14px] leading-relaxed text-ivory/65'>
                            Share the essentials and we will come back to you personally. Fields without a note are required.
                        </p>

                        <form ref={formRef} noValidate onSubmit={handleSubmit} className='mt-9 grid gap-x-8 gap-y-7 md:grid-cols-2'>
                            <Field id='fullName' label='Full name' error={errors.fullName}>
                                <input
                                    id='booking-fullName'
                                    name='fullName'
                                    autoComplete='name'
                                    value={values.fullName}
                                    onChange={update('fullName')}
                                    aria-invalid={Boolean(errors.fullName)}
                                    aria-describedby={describedBy('fullName')}
                                    className={inputClass}
                                    placeholder='Your name'
                                />
                            </Field>

                            <Field id='email' label='Email' error={errors.email}>
                                <input
                                    id='booking-email'
                                    name='email'
                                    type='email'
                                    autoComplete='email'
                                    value={values.email}
                                    onChange={update('email')}
                                    aria-invalid={Boolean(errors.email)}
                                    aria-describedby={describedBy('email')}
                                    className={inputClass}
                                    placeholder='you@example.com'
                                />
                            </Field>

                            <Field id='phone' label='Phone' optional error={errors.phone}>
                                <input
                                    id='booking-phone'
                                    name='phone'
                                    type='tel'
                                    autoComplete='tel'
                                    value={values.phone}
                                    onChange={update('phone')}
                                    aria-invalid={Boolean(errors.phone)}
                                    aria-describedby={describedBy('phone')}
                                    className={inputClass}
                                    placeholder='Include your country code'
                                />
                            </Field>

                            <Field id='passengers' label='Passengers' error={errors.passengers}>
                                <select
                                    id='booking-passengers'
                                    name='passengers'
                                    value={values.passengers}
                                    onChange={update('passengers')}
                                    aria-invalid={Boolean(errors.passengers)}
                                    aria-describedby={describedBy('passengers')}
                                    className={cn(inputClass, 'cursor-pointer appearance-none bg-onyx')}
                                >
                                    {Array.from({ length: 19 }, (_, index) => index + 1).map((count) => (
                                        <option key={count} value={count}>
                                            {count === 1 ? '1 passenger' : `${count} passengers`}
                                        </option>
                                    ))}
                                </select>
                            </Field>

                            <Field id='departure' label='Flying from' error={errors.departure}>
                                <input
                                    id='booking-departure'
                                    name='departure'
                                    value={values.departure}
                                    onChange={update('departure')}
                                    aria-invalid={Boolean(errors.departure)}
                                    aria-describedby={describedBy('departure')}
                                    className={inputClass}
                                    placeholder='City or airport'
                                />
                            </Field>

                            <Field id='destination' label='Flying to' error={errors.destination}>
                                <input
                                    id='booking-destination'
                                    name='destination'
                                    value={values.destination}
                                    onChange={update('destination')}
                                    aria-invalid={Boolean(errors.destination)}
                                    aria-describedby={describedBy('destination')}
                                    className={inputClass}
                                    placeholder='City or airport'
                                />
                            </Field>

                            <Field id='travelDate' label='Travel date' error={errors.travelDate}>
                                <input
                                    id='booking-travelDate'
                                    name='travelDate'
                                    type='date'
                                    min={minDate || undefined}
                                    value={values.travelDate}
                                    onChange={update('travelDate')}
                                    aria-invalid={Boolean(errors.travelDate)}
                                    aria-describedby={describedBy('travelDate')}
                                    className={cn(inputClass, 'field-date [color-scheme:dark]')}
                                />
                            </Field>

                            <Field id='notes' label='Anything we should know' optional className='md:col-span-2'>
                                <textarea
                                    id='booking-notes'
                                    name='notes'
                                    rows={3}
                                    value={values.notes}
                                    onChange={update('notes')}
                                    className={cn(inputClass, 'h-auto resize-none py-3 leading-relaxed')}
                                    placeholder='Return date, pets, catering, ground transport'
                                />
                            </Field>

                            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                                <label htmlFor='booking-company'>Company</label>
                                <input
                                    id='booking-company'
                                    name='company'
                                    tabIndex={-1}
                                    autoComplete='off'
                                    value={values.company}
                                    onChange={update('company')}
                                />
                            </div>

                            <div className='flex flex-col gap-4 md:col-span-2 md:flex-row md:items-center md:justify-between'>
                                <p className='max-w-sm text-[12px] leading-relaxed text-smoke'>
                                    We use your details only to arrange your flight.
                                </p>
                                <button
                                    type='submit'
                                    disabled={status === 'sending'}
                                    className='inline-flex h-14 items-center justify-center gap-3 rounded-full bg-champagne px-8 text-[12px] uppercase tracking-[0.22em] text-obsidian transition-colors hover:bg-champagne-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-champagne disabled:cursor-wait disabled:opacity-70'
                                >
                                    {status === 'sending' ? (
                                        <>
                                            <Loader2 className='size-4 animate-spin' />
                                            Sending
                                        </>
                                    ) : (
                                        <>
                                            Send request
                                            <ArrowUpRight className='size-4' />
                                        </>
                                    )}
                                </button>
                            </div>

                            {status === 'error' && message && (
                                <p role='alert' className='text-[13px] text-[#e39a8c] md:col-span-2'>
                                    {message}
                                </p>
                            )}
                        </form>
                    </>
                )}
            </div>
        </dialog>
    )
}

export default BookingDialog
